import type { ICampaignMonitorSearch } from '@modules/campaignMonitorReport/types';

import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  FIELD_FILTERS_ENTITY,
  type ISearchParameters,
  OPERATOR_SORT,
  OPERATOR_TYPE,
} from '@features/filters/types';

interface IInitialState {
  isAllRowsExpanded: boolean;
  searchParameters: ISearchParameters;
  expandedRows: Record<string, boolean>;
}

export const initialCampaignMonitorSearchParameters = {
  page: 1,
  size: 10,
  sorts: [
    {
      order: OPERATOR_SORT.DESC,
      field: 'trigger_received_at',
    },
  ],
  filters: [
    {
      value: false,
      type: OPERATOR_TYPE.EQUAL,
      field: FIELD_FILTERS_ENTITY.HIDDEN,
    },
  ],
};

const initialState: IInitialState = {
  expandedRows: {},
  isAllRowsExpanded: false,
  searchParameters: initialCampaignMonitorSearchParameters,
};

const campaignMonitorSlice = createSlice({
  initialState,
  name: 'campaignMonitorSlice',
  reducers: {
    resetExpandedRows(state: IInitialState) {
      state.expandedRows = initialState.expandedRows;
    },
    setIsAllRowsExpanded(state: IInitialState, action: PayloadAction<boolean>) {
      state.isAllRowsExpanded = action.payload;
    },
    setSearchParameters(
      state: IInitialState,
      action: PayloadAction<Partial<ISearchParameters>>
    ) {
      state.searchParameters = { ...state.searchParameters, ...action.payload };
    },
    updateExpandedRows(
      state: IInitialState,
      action: PayloadAction<Record<string, boolean>>
    ) {
      state.expandedRows = {
        ...state.expandedRows,
        ...action.payload,
      };
    },
    updateExpandedRowsWithSearchResponse(
      state: IInitialState,
      action: PayloadAction<ICampaignMonitorSearch>
    ) {
      if (state.isAllRowsExpanded) {
        const rowIds = action.payload.data?.map(({ id }) => id) || [];
        const updatedConfig = Object.fromEntries(
          rowIds.map((id) => [id, true])
        );

        state.expandedRows = {
          ...state.expandedRows,
          ...updatedConfig,
        };
      }
    },
  },
});

export default campaignMonitorSlice.reducer;

export const {
  resetExpandedRows,
  updateExpandedRows,
  setSearchParameters,
  setIsAllRowsExpanded,
  updateExpandedRowsWithSearchResponse,
} = campaignMonitorSlice.actions;
